import { Link, useParams } from "react-router-dom";
import { useEffect, useState, type ReactNode } from "react";
import { useLang } from "../i18n/useT";
import { useStore } from "../store/store";
import { POSTS, findPost, type Post } from "../data/posts";
import { useReveal } from "../lib/useReveal";
import { PulseGlyph } from "../components/Logo";

/* ---------- shared marketing-style chrome ---------- */
function MarketingHeader() {
  const lang = useLang();
  const setLanguage = useStore((s) => s.setLanguage);
  return (
    <header className="sticky top-0 z-40 border-b border-slate-200/70 bg-white/85 backdrop-blur">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-3">
        <Link to="/" className="flex items-center gap-2">
          <div className="relative grid h-9 w-9 place-items-center overflow-hidden rounded-lg bg-ink-900">
            <PulseGlyph />
            <span aria-hidden className="absolute -right-2 -top-2 h-4 w-4 rounded-full bg-accent-400" />
            <span aria-hidden className="absolute -bottom-2 -left-2 h-4 w-4 rounded-full bg-brand-400" />
          </div>
          <div className="leading-tight">
            <div className="font-semibold">Pulso Reach</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">
              {lang === "es" ? "Firma de investigación multilingüe" : "Multilingual research firm"}
            </div>
          </div>
        </Link>
        <nav className="hidden items-center gap-7 text-sm font-medium text-slate-600 md:flex">
          <Link to="/#about" className="hover:text-ink-900">{lang === "es" ? "Quiénes somos" : "About"}</Link>
          <Link to="/#polls" className="hover:text-ink-900">{lang === "es" ? "Tipos de encuestas" : "Poll types"}</Link>
          <Link to="/#method" className="hover:text-ink-900">{lang === "es" ? "Metodología" : "Methodology"}</Link>
          <Link to="/blog" className="text-ink-900">Blog</Link>
          <Link to="/#research" className="hover:text-ink-900">{lang === "es" ? "Investigación" : "Research"}</Link>
        </nav>
        <div className="flex items-center gap-2">
          <div className="hidden items-center gap-1 sm:flex">
            <button onClick={() => setLanguage("en")} className={`rounded px-2 py-1 text-xs font-semibold transition ${lang === "en" ? "bg-ink-900 text-white" : "text-slate-600 hover:text-ink-900"}`}>EN</button>
            <button onClick={() => setLanguage("es")} className={`rounded px-2 py-1 text-xs font-semibold transition ${lang === "es" ? "bg-ink-900 text-white" : "text-slate-600 hover:text-ink-900"}`}>ES</button>
          </div>
          <Link to="/app/dashboard" className="group inline-flex items-center gap-1.5 rounded-full bg-ink-900 px-4 py-2 text-sm font-semibold text-white transition hover:bg-brand-700">
            {lang === "es" ? "Abrir demo" : "Launch demo"}
            <span aria-hidden className="transition-transform group-hover:translate-x-0.5">→</span>
          </Link>
        </div>
      </div>
    </header>
  );
}

function MarketingFooter() {
  const lang = useLang();
  return (
    <footer className="border-t border-slate-200 bg-white">
      <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-3 px-6 py-8 text-xs text-slate-500 sm:flex-row">
        <div>© {new Date().getFullYear()} Pulso Reach · {lang === "es" ? "Demo de plataforma de investigación. Construido con cumplimiento desde el primer día." : "Research-platform demo. Built compliance-first."}</div>
        <a href="https://github.com/Mdr-palacios/mensajeando" target="_blank" rel="noopener noreferrer" className="hover:text-ink-900">
          {lang === "es" ? "Código fuente" : "Source code"} ↗
        </a>
      </div>
    </footer>
  );
}

function Reveal({ children, delay = 0 }: { children: ReactNode; delay?: number }) {
  const { ref, shown } = useReveal<HTMLDivElement>();
  return (
    <div
      ref={ref}
      style={{ transitionDelay: `${delay}ms` }}
      className={`transition-all duration-700 ease-out ${shown ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`}
    >
      {children}
    </div>
  );
}

/* ---------- date format ---------- */
function formatDate(iso: string, lang: "en" | "es") {
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString(lang === "es" ? "es-MX" : "en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

/* ---------- tiny markdown-ish renderer ---------- */
function renderInline(text: string): ReactNode[] {
  // [text](url) and `code` and **bold**
  const nodes: ReactNode[] = [];
  const regex = /\[([^\]]+)\]\(([^)]+)\)|`([^`]+)`|\*\*([^*]+)\*\*/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let key = 0;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    if (m[1] && m[2]) {
      nodes.push(<a key={key++} href={m[2]} target="_blank" rel="noopener noreferrer" className="text-brand-700 underline-offset-4 hover:underline">{m[1]}</a>);
    } else if (m[3]) {
      nodes.push(<code key={key++} className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[0.9em] text-ink-900">{m[3]}</code>);
    } else if (m[4]) {
      nodes.push(<strong key={key++} className="font-semibold text-ink-900">{m[4]}</strong>);
    }
    last = regex.lastIndex;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

function renderBody(body: string): ReactNode {
  const lines = body.split("\n");
  const out: ReactNode[] = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i] ?? "";

    if (line.startsWith("```")) {
      const buf: string[] = [];
      i++;
      while (i < lines.length && !(lines[i] ?? "").startsWith("```")) { buf.push(lines[i] ?? ""); i++; }
      i++; // skip closing
      out.push(
        <pre key={key++} className="my-6 overflow-x-auto rounded-xl border border-slate-200 bg-slate-50 p-4 text-xs leading-relaxed text-ink-900">
          <code>{buf.join("\n")}</code>
        </pre>
      );
      continue;
    }

    if (line.startsWith("## ")) {
      out.push(<h2 key={key++} className="mt-12 text-2xl font-bold tracking-tight text-ink-900 sm:text-3xl">{renderInline(line.slice(3))}</h2>);
      i++;
      continue;
    }
    if (line.startsWith("### ")) {
      out.push(<h3 key={key++} className="mt-8 text-xl font-semibold tracking-tight text-ink-900">{renderInline(line.slice(4))}</h3>);
      i++;
      continue;
    }
    if (line.startsWith("> ")) {
      // collect consecutive quote lines
      const buf: string[] = [line.slice(2)];
      i++;
      while (i < lines.length && (lines[i] ?? "").startsWith("> ")) { buf.push((lines[i] ?? "").slice(2)); i++; }
      out.push(
        <blockquote key={key++} className="my-8 border-l-4 border-accent-400 bg-accent-50/60 px-5 py-4 text-lg italic leading-relaxed text-ink-900">
          {renderInline(buf.join(" "))}
        </blockquote>
      );
      continue;
    }
    if (line.startsWith("- ")) {
      const items: string[] = [line.slice(2)];
      i++;
      while (i < lines.length && (lines[i] ?? "").startsWith("- ")) { items.push((lines[i] ?? "").slice(2)); i++; }
      out.push(
        <ul key={key++} className="my-5 space-y-2 pl-5 text-slate-700">
          {items.map((it, k) => (
            <li key={k} className="relative leading-relaxed marker:text-brand-500">
              <span className="absolute -left-5 top-2 inline-block h-1.5 w-1.5 rounded-full bg-brand-500" />
              {renderInline(it)}
            </li>
          ))}
        </ul>
      );
      continue;
    }
    if (/^\d+\.\s/.test(line)) {
      const items: string[] = [line.replace(/^\d+\.\s/, "")];
      i++;
      while (i < lines.length && /^\d+\.\s/.test(lines[i] ?? "")) { items.push((lines[i] ?? "").replace(/^\d+\.\s/, "")); i++; }
      out.push(
        <ol key={key++} className="my-5 list-decimal space-y-2 pl-6 text-slate-700 marker:font-semibold marker:text-ink-900">
          {items.map((it, k) => <li key={k} className="leading-relaxed">{renderInline(it)}</li>)}
        </ol>
      );
      continue;
    }
    if (line.trim() === "") {
      i++;
      continue;
    }
    // paragraph (collect until blank)
    const buf: string[] = [line];
    i++;
    while (i < lines.length && (lines[i] ?? "").trim() !== "" && !(lines[i] ?? "").startsWith("#") && !(lines[i] ?? "").startsWith("- ") && !(lines[i] ?? "").startsWith("> ") && !(lines[i] ?? "").startsWith("```") && !/^\d+\.\s/.test(lines[i] ?? "")) {
      buf.push(lines[i] ?? "");
      i++;
    }
    out.push(<p key={key++} className="my-5 text-lg leading-relaxed text-slate-700">{renderInline(buf.join(" "))}</p>);
  }

  return <>{out}</>;
}

/* ----------------------------------------------------------------- */
/*                              Index                                 */
/* ----------------------------------------------------------------- */
export function BlogIndex() {
  const lang = useLang();

  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-full bg-white text-ink-900 selection:bg-accent-300 selection:text-ink-900">
      <MarketingHeader />

      {/* Hero */}
      <section className="relative isolate overflow-hidden">
        <div aria-hidden className="pointer-events-none absolute -right-24 -top-20 h-[420px] w-[420px] rounded-full bg-brand-300/30 blur-3xl" />
        <div aria-hidden className="pointer-events-none absolute -left-20 top-32 h-64 w-64 rounded-full bg-accent-300/40 blur-2xl" />
        <div className="mx-auto max-w-5xl px-6 py-20">
          <div className="inline-flex items-center gap-2 rounded-full border border-ink-900/10 bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-widest text-ink-900 shadow-sm">
            <span className="h-1.5 w-1.5 rounded-full bg-brand-500" />
            {lang === "es" ? "Notas de campo" : "Field notes"}
          </div>
          <h1 className="mt-5 text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl">
            {lang === "es" ? (
              <>Cómo encuestar a <span className="relative inline-block">
                <span className="relative z-10">latinos</span>
                <span aria-hidden className="absolute inset-x-0 bottom-1 z-0 h-3 bg-accent-300" />
              </span>, de verdad.</>
            ) : (
              <>How to actually poll <span className="relative inline-block">
                <span className="relative z-10">Latinos</span>
                <span aria-hidden className="absolute inset-x-0 bottom-1 z-0 h-3 bg-accent-300" />
              </span>.</>
            )}
          </h1>
          <p className="mt-5 max-w-2xl text-lg text-slate-700">
            {lang === "es"
              ? "Ensayos cortos sobre las técnicas, los sesgos y las decisiones de diseño que hacen (o rompen) la investigación de encuestas con poblaciones latinas en EE. UU.; la base de nuestro método multilingüe."
              : "Short essays on the techniques, biases, and design decisions that make (or break) survey research with U.S. Latino populations; the foundation of our multilingual method."}
          </p>
        </div>
      </section>

      {/* Posts grid */}
      <section className="border-t border-slate-200 bg-white">
        <div className="mx-auto max-w-6xl px-6 py-16">
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {POSTS.map((p, i) => (
              <Reveal key={p.slug} delay={(i % 3) * 90}>
                <PostCard post={p} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}

function PostCard({ post }: { post: Post }) {
  const lang = useLang();
  const [loaded, setLoaded] = useState(false);
  return (
    <Link
      to={`/blog/${post.slug}`}
      className="group flex h-full flex-col overflow-hidden rounded-2xl border border-ink-900/10 bg-white transition hover:-translate-y-1 hover:shadow-xl"
    >
      <div className="relative aspect-[16/10] overflow-hidden bg-slate-100">
        <img
          src={post.image}
          alt=""
          loading="lazy"
          onLoad={() => setLoaded(true)}
          className={`h-full w-full object-cover transition duration-700 ${loaded ? "opacity-100 scale-100" : "opacity-0 scale-105"} group-hover:scale-105`}
        />
        <div className="absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-ink-900/85 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-widest text-white backdrop-blur">
          <span className="h-1 w-1 rounded-full bg-accent-400" />
          {post.category[lang]}
        </div>
      </div>
      <div className="flex flex-1 flex-col p-5">
        <div className="text-[11px] font-medium uppercase tracking-widest text-slate-400">
          {formatDate(post.date, lang)} · {post.readMinutes} {lang === "es" ? "min" : "min"} {lang === "es" ? "lectura" : "read"}
        </div>
        <h3 className="mt-2 line-clamp-3 text-lg font-semibold leading-snug tracking-tight text-ink-900 group-hover:text-brand-700">
          {post.title[lang]}
        </h3>
        <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-slate-600">{post.excerpt[lang]}</p>
        <div className="mt-auto pt-4 text-xs font-semibold text-brand-700">
          {lang === "es" ? "Leer artículo" : "Read article"} →
        </div>
      </div>
    </Link>
  );
}

/* ----------------------------------------------------------------- */
/*                              Detail                                */
/* ----------------------------------------------------------------- */
export function BlogPost() {
  const { slug = "" } = useParams();
  const lang = useLang();
  const post = findPost(slug);

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  // Reading progress
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement;
      const scrolled = h.scrollTop;
      const max = h.scrollHeight - h.clientHeight;
      setProgress(max > 0 ? Math.min(1, scrolled / max) : 0);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (!post) {
    return (
      <div className="min-h-full bg-white">
        <MarketingHeader />
        <div className="mx-auto max-w-3xl px-6 py-24 text-center">
          <h1 className="text-2xl font-semibold">{lang === "es" ? "Artículo no encontrado" : "Article not found"}</h1>
          <Link to="/blog" className="mt-6 inline-block text-brand-700 hover:underline">{lang === "es" ? "Volver al blog" : "Back to blog"}</Link>
        </div>
        <MarketingFooter />
      </div>
    );
  }

  const otherPosts = POSTS.filter((p) => p.slug !== post.slug).slice(0, 3);

  return (
    <div className="min-h-full bg-white text-ink-900 selection:bg-accent-300 selection:text-ink-900">
      {/* Reading progress bar */}
      <div className="fixed left-0 top-0 z-50 h-1 w-full bg-transparent">
        <div className="h-full bg-accent-400 transition-[width] duration-150" style={{ width: `${progress * 100}%` }} />
      </div>

      <MarketingHeader />

      {/* Hero */}
      <section className="relative isolate overflow-hidden">
        <div aria-hidden className="pointer-events-none absolute -right-24 -top-24 h-[420px] w-[420px] rounded-full bg-brand-300/30 blur-3xl" />
        <div aria-hidden className="pointer-events-none absolute -left-20 top-40 h-64 w-64 rounded-full bg-accent-300/40 blur-2xl" />

        <div className="mx-auto max-w-3xl px-6 pt-16 pb-10">
          <Link to="/blog" className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-ink-900">
            <span aria-hidden>←</span> {lang === "es" ? "Todos los artículos" : "All articles"}
          </Link>
          <div className="mt-6 inline-flex items-center gap-1.5 rounded-full bg-ink-900 px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-white">
            <span className="h-1 w-1 rounded-full bg-accent-400" />
            {post.category[lang]}
          </div>
          <h1 className="mt-4 text-4xl font-bold leading-[1.1] tracking-tight sm:text-5xl">{post.title[lang]}</h1>
          <p className="mt-5 text-lg leading-relaxed text-slate-600">{post.excerpt[lang]}</p>
          <div className="mt-6 flex items-center gap-3 text-sm text-slate-500">
            <span>{formatDate(post.date, lang)}</span>
            <span aria-hidden>·</span>
            <span>{post.readMinutes} {lang === "es" ? "min de lectura" : "min read"}</span>
          </div>
        </div>

        <div className="mx-auto max-w-5xl px-6">
          <div className="relative overflow-hidden rounded-3xl border border-ink-900/10 bg-slate-100" style={{ aspectRatio: "16 / 8" }}>
            <img src={post.image} alt="" className="absolute inset-0 h-full w-full object-cover" />
            <div aria-hidden className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
          </div>
        </div>
      </section>

      {/* Body */}
      <article className="mx-auto max-w-3xl px-6 py-14">
        {renderBody(post.body[lang])}

        {/* Citations */}
        <hr className="my-12 border-slate-200" />
        <h3 className="text-sm font-semibold uppercase tracking-widest text-slate-500">
          {lang === "es" ? "Fuentes" : "Sources"}
        </h3>
        <ol className="mt-4 space-y-3 text-sm">
          {post.citations.map((c, i) => (
            <li key={i} className="rounded-lg border border-slate-200 bg-white p-4">
              <span className="text-slate-700">{c.label}</span>{" "}
              <a href={c.url} target="_blank" rel="noopener noreferrer" className="break-all text-brand-700 underline-offset-4 hover:underline">
                {c.url}
              </a>
            </li>
          ))}
        </ol>
      </article>

      {/* Related */}
      {otherPosts.length > 0 && (
        <section className="border-t border-slate-200 bg-slate-50">
          <div className="mx-auto max-w-6xl px-6 py-16">
            <h3 className="text-sm font-semibold uppercase tracking-widest text-slate-500">
              {lang === "es" ? "Sigue leyendo" : "Keep reading"}
            </h3>
            <div className="mt-6 grid gap-6 md:grid-cols-3">
              {otherPosts.map((p) => (
                <PostCard key={p.slug} post={p} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Demo CTA strip */}
      <section className="bg-white">
        <div className="mx-auto max-w-6xl px-6 py-16">
          <div className="relative overflow-hidden rounded-3xl bg-ink-900 px-8 py-12 text-white sm:px-12">
            <div aria-hidden className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-brand-500/40 blur-3xl" />
            <div aria-hidden className="absolute -bottom-20 -left-10 h-64 w-64 rounded-full bg-accent-400/30 blur-3xl" />
            <div className="relative flex flex-col items-start gap-5 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h3 className="text-2xl font-bold tracking-tight sm:text-3xl">
                  {lang === "es" ? "Ve estas técnicas en la plataforma." : "See these techniques in the platform."}
                </h3>
                <p className="mt-2 max-w-xl text-white/80">
                  {lang === "es"
                    ? "El demo en vivo incluye enrutamiento por idioma, batería demográfica multidimensional y registro de consentimiento."
                    : "The live demo includes language routing, the multi-dimensional demographic battery, and the consent ledger."}
                </p>
              </div>
              <Link to="/app/dashboard" className="group inline-flex shrink-0 items-center gap-2 rounded-full bg-accent-400 px-5 py-3 text-sm font-semibold text-ink-900 transition hover:bg-accent-300">
                {lang === "es" ? "Abrir demo" : "Launch demo"}
                <span aria-hidden className="transition-transform group-hover:translate-x-0.5">→</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
